import { type IPoint } from 'fabric/fabric-impl';
import { AttachmentPoint } from '../types';
import { sqrDistanceBetweenPoints } from './geometry';
import { hasConnectorAttachmentBehaviours } from '../objects/guards';
import { type Attachable } from '../objects/Connector';

// Scale factor along given axis to determine how far to search for nearby shapes
const SEARCH_DISTANCE_FACTOR = 3;
// Percentage of off-axis dimension to use as deviation threshold
const OFF_AXIS_DEVIATION_FACTOR = 0.5;

/**
 * Find the nearest connectable shape in the direction of the given normal vector
 */
export function findNearbyConnectableShape(
  sourceShape: Attachable,
  controlPosition: IPoint,
  normal: IPoint
): Attachable | null {
  const canvas = sourceShape.canvas;
  if (!canvas) return null;

  // Search distance is based on dimension along axis of this control, which
  // we get from normal
  const searchDistance =
    normal.x !== 0
      ? sourceShape.getScaledWidth() * SEARCH_DISTANCE_FACTOR
      : sourceShape.getScaledHeight() * SEARCH_DISTANCE_FACTOR;

  // Off-axis deviation based on percentage of the off-axis dimension
  const offAxisDeviation =
    (normal.x !== 0
      ? sourceShape.getScaledHeight()
      : sourceShape.getScaledWidth()) * OFF_AXIS_DEVIATION_FACTOR;

  // Find all connectable shapes in the search area
  const candidates: Attachable[] = canvas
    .getObjects()
    .filter(obj => hasConnectorAttachmentBehaviours(obj))
    .filter(obj => {
      if (obj.attachmentPoints?.().length === 0) return false;

      // Must not be the source shape
      if (obj === sourceShape) return false;
      // Must not already be connected to the source shape
      if (areObjectsAlreadyConnected(sourceShape, obj)) return false;

      // Check if shape is directly in the direction of the normal vector
      const objCenter = obj.getCenterPoint();
      const deltaX = objCenter.x - controlPosition.x;
      const deltaY = objCenter.y - controlPosition.y;

      // For horizontal controls (left/right), only consider shapes in that direction
      if (normal.x !== 0) {
        // Must be in the correct horizontal direction
        if (normal.x > 0 && deltaX <= 0) return false;
        if (normal.x < 0 && deltaX >= 0) return false;

        // Must be within search distance along this axis
        if (Math.abs(deltaX) > searchDistance) return false;

        // Only consider shapes within a narrow vertical band
        const verticalOffset = Math.abs(deltaY);
        return verticalOffset <= offAxisDeviation;
      }

      // For vertical controls (top/bottom), only consider shapes in that direction
      if (normal.y !== 0) {
        // Must be in the correct vertical direction
        if (normal.y > 0 && deltaY <= 0) return false;
        if (normal.y < 0 && deltaY >= 0) return false;

        // Must be within search distance along this axis
        if (Math.abs(deltaY) > searchDistance) return false;

        // Only consider shapes within a narrow horizontal band
        const horizontalDeviation = Math.abs(deltaX);
        return horizontalDeviation <= offAxisDeviation;
      }

      return false;
    });

  if (candidates.length === 0) return null;

  // Get closest shape using single-pass minimum finding
  let closest: Attachable | null = null;
  let closestDist = Infinity;

  for (const current of candidates) {
    const currentDist = sqrDistanceBetweenPoints(
      current.getCenterPoint(),
      controlPosition.x,
      controlPosition.y
    );
    if (currentDist < closestDist) {
      closestDist = currentDist;
      closest = current;
    }
  }

  return closest;
}

/**
 * Check if two shapes are already connected via a shared connector. Technically
 * not a very efficient implementation, but in practice objects do not have very
 * many connections
 */
export function areObjectsAlreadyConnected(
  shape1: Attachable,
  shape2: Attachable
): boolean {
  // Check if both shapes have connector attachment behaviors
  const shape1Attachments = shape1.connectorAttachments;
  const shape2Attachments = shape2.connectorAttachments;

  if (!shape1Attachments || !shape2Attachments) return false;

  // Check if they share any connectors
  return shape1Attachments.some(a1 =>
    shape2Attachments.some(a2 => a1.connector === a2.connector)
  );
}

/**
 * Determine the best attachment point on the target shape for a connector
 */
export function getTargetAttachmentPoint(
  targetShape: Attachable,
  sourceAttachmentPoint: AttachmentPoint
): AttachmentPoint | null {
  const attachmentPoints = targetShape
    .attachmentPoints?.()
    .filter(ap => ap !== AttachmentPoint.Edge);

  if (!attachmentPoints || attachmentPoints.length === 0) {
    return null;
  }

  // Prefer center point if available. This reduces the need for determining
  // the closest attachment point, since center gives us that for free, along
  // with the auto-adjustment behavior center point covers
  if (attachmentPoints.includes(AttachmentPoint.Center)) {
    return AttachmentPoint.Center;
  }

  // Otherwise use the attachment point opposite to the source
  const oppositeMap = {
    [AttachmentPoint.Left]: AttachmentPoint.Right,
    [AttachmentPoint.Right]: AttachmentPoint.Left,
    [AttachmentPoint.Top]: AttachmentPoint.Bottom,
    [AttachmentPoint.Bottom]: AttachmentPoint.Top,
  };

  const opposite = oppositeMap[sourceAttachmentPoint];
  if (opposite && attachmentPoints.includes(opposite)) {
    return opposite;
  }

  // Fallback to first available
  return attachmentPoints[0];
}
